import { HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { ApiResponse } from '../../core/models';
import { AuthStateService, BaseApiService } from '../../core/services';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { MenuTreeDTO, SidebarMenuGroup, SidebarMenuItem } from '../models/menu-tree.model';

@Injectable({
  providedIn: 'root',
})
export class UserMenusService extends BaseApiService {
  private readonly controllerUrl = '/UserPermissions/';
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly authState = inject(AuthStateService);

  readonly sidebarMenus = signal<SidebarMenuGroup[]>([]);
  readonly isLoading = signal(false);
  private publicMenusLoaded = false;
  private publicMenusRequest: Promise<void> | null = null;

  async loadMenus(forceRefresh = false): Promise<void> {
    if (this.authState.isGuest()) {
      return this.loadPublicMenus(forceRefresh);
    }

    this.publicMenusLoaded = false;

    if (!forceRefresh) {
      const cachedMenus = this.sessionStorage.getMenus() as MenuTreeDTO[] | null;
      if (cachedMenus?.length) {
        this.sidebarMenus.set(this.transformMenuTree(cachedMenus));
        return;
      }
    }

    this.isLoading.set(true);

    try {
      const response = await firstValueFrom(this.fetchMenuTree());

      if (response.body?.isSuccess && response.body.data) {
        this.sessionStorage.setMenus(response.body.data);
        this.sidebarMenus.set(this.transformMenuTree(response.body.data));
        return;
      }

      console.warn('No menu data received from API');
      this.sidebarMenus.set([]);
    } catch (error) {
      console.error('Error loading menus from API:', error);
      this.sidebarMenus.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  clearMenus(): void {
    this.publicMenusLoaded = false;
    this.publicMenusRequest = null;
    this.sidebarMenus.set([]);
    this.sessionStorage.setMenus([]);
  }

  canAccessRoute(routePath: string): boolean {
    const normalizedRoute = this.normalizeRoutePath(routePath);
    if (!normalizedRoute) {
      return false;
    }

    if (UserMenusService.ALWAYS_ALLOWED_ROUTES.has(normalizedRoute)) {
      return true;
    }

    const allowedRoutes = this.getAllowedRoutesFromMenus();

    if (allowedRoutes.has(normalizedRoute)) {
      return true;
    }

    const aliasRoutes = UserMenusService.ROUTE_ACCESS_ALIASES[normalizedRoute];
    return aliasRoutes?.some((route) => allowedRoutes.has(route)) ?? false;
  }

  private static readonly ALWAYS_ALLOWED_ROUTES = new Set([
    'home',
  ]);

  /** Rutas sin ítem de menú que heredan acceso de otras rutas. */
  private static readonly ROUTE_ACCESS_ALIASES: Record<string, readonly string[]> = {
    'payment-wizard': ['service-operations'],
  };

  /** Carga anónima del árbol público usado por la sesión invitada. */
  private loadPublicMenus(forceRefresh: boolean): Promise<void> {
    const cachedMenus = this.sessionStorage.getMenus() as MenuTreeDTO[] | null;

    // Mantener visible el menú restaurado mientras se valida/actualiza contra
    // la API. Esto evita que desaparezca durante el retorno de la pasarela.
    if (!this.sidebarMenus().length && cachedMenus?.length) {
      this.sidebarMenus.set(this.transformMenuTree(cachedMenus));
    }

    if (!forceRefresh && this.publicMenusLoaded && this.sidebarMenus().length) {
      return Promise.resolve();
    }

    if (this.publicMenusRequest) {
      return this.publicMenusRequest;
    }

    this.isLoading.set(true);
    this.publicMenusRequest = (async () => {
      try {
        const response = await firstValueFrom(this.fetchPublicMenuTree());

        if (!response.body?.isSuccess) {
          throw new Error(
            response.body?.message ?? 'Unable to load public menus',
          );
        }

        const menus = response.body.data ?? [];

        // Una respuesta vacía aislada no debe reemplazar un árbol público
        // que ya sabemos válido. Se deja como no cargado para reintentar en la
        // próxima navegación.
        if (!menus.length && this.sidebarMenus().length) {
          this.publicMenusLoaded = false;
          return;
        }

        this.publicMenusLoaded = true;

        if (!this.authState.isGuest()) {
          return;
        }

        this.sessionStorage.setMenus(menus);
        this.sidebarMenus.set(this.transformMenuTree(menus));
      } catch (error) {
        console.error('Error loading public menus from API:', error);
        // Un fallo transitorio no debe borrar un menú guest que ya fue
        // cargado correctamente en esta sesión.
      } finally {
        this.isLoading.set(false);
        this.publicMenusRequest = null;
      }
    })();

    return this.publicMenusRequest;
  }

  private fetchMenuTree(): Observable<HttpResponse<ApiResponse<MenuTreeDTO[]>>> {
    return this.http.get<ApiResponse<MenuTreeDTO[]>>(
      `${this.baseUrl}${this.controllerUrl}menus-tree`,
      { observe: 'response' },
    );
  }

  private fetchPublicMenuTree(): Observable<HttpResponse<ApiResponse<MenuTreeDTO[]>>> {
    return this.http.get<ApiResponse<MenuTreeDTO[]>>(
      `${this.baseUrl}${this.controllerUrl}public-menus-tree`,
      { observe: 'response' },
    );
  }

  private transformMenuTree(menuData: MenuTreeDTO[]): SidebarMenuGroup[] {
    return [...menuData]
      .sort((a, b) => a.order - b.order)
      .map((menu) => this.toSidebarGroup(menu))
      .filter((group): group is SidebarMenuGroup => group !== null);
  }

  private toSidebarGroup(menu: MenuTreeDTO): SidebarMenuGroup | null {
    const children = [...menu.children]
      .sort((a, b) => a.order - b.order)
      .map((child) => this.toSidebarItem(child))
      .filter((child): child is SidebarMenuItem => child !== null);

    if (children.length > 0) {
      return {
        code: menu.code,
        name: menu.name,
        icon: menu.icon ?? 'folder',
        children,
      };
    }

    const route = this.normalizeRoute(menu.url);
    if (!route) {
      return null;
    }

    return {
      code: menu.code,
      name: menu.name,
      icon: menu.icon ?? 'link',
      children: [
        {
          code: menu.code,
          name: menu.name,
          route,
          icon: menu.icon ?? 'link',
          children: [],
        },
      ],
    };
  }

  private toSidebarItem(menu: MenuTreeDTO): SidebarMenuItem | null {
    const route = this.normalizeRoute(menu.url);
    const children = [...menu.children]
      .sort((a, b) => a.order - b.order)
      .map((child) => this.toSidebarItem(child))
      .filter((child): child is SidebarMenuItem => child !== null);

    if (!route && children.length === 0) {
      return null;
    }

    return {
      code: menu.code,
      name: menu.name,
      route,
      icon: menu.icon ?? 'link',
      children,
    };
  }

  private normalizeRoute(url: string | null): string | null {
    if (!url || url === '-') {
      return null;
    }

    return this.normalizeRoutePath(url);
  }

  private normalizeRoutePath(routePath: string): string | null {
    const normalized = routePath.trim().replace(/^\//, '').replace(/^itxap\//i, '');
    return normalized || null;
  }

  private getAllowedRoutesFromMenus(): Set<string> {
    const routes = new Set<string>();

    for (const group of this.sidebarMenus()) {
      this.collectAllowedRoutes(group.children, routes);
    }

    return routes;
  }

  private collectAllowedRoutes(
    items: SidebarMenuItem[],
    routes: Set<string>,
  ): void {
    for (const item of items) {
      if (item.route) {
        const normalized = this.normalizeRoutePath(item.route);
        if (normalized) {
          routes.add(normalized);
        }
      }

      this.collectAllowedRoutes(item.children, routes);
    }
  }
}
